/**
 * POS-SUPPLIERS-FIN-003 — UI mínima cuenta corriente proveedor
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { matrixAllows } from '../../utils/matrixAllows';
import proveedoresService from '../../services/proveedores.service';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';

function money(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

const ProveedorCuentaCorriente = () => {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { permisosEfectivos, sucursalSeleccionada, sucursalesDisponibles } = useAuth();
  const canView = matrixAllows(permisosEfectivos, 'proveedores', 'cuenta_corriente.ver') === true;
  const canInvoice = matrixAllows(permisosEfectivos, 'proveedores', 'facturas.crear') === true;
  const canPay = matrixAllows(permisosEfectivos, 'compras', 'pagar') === true;
  const canCreatePurchase = matrixAllows(permisosEfectivos, 'compras', 'crear') === true;
  const canOperateCaja = matrixAllows(permisosEfectivos, 'caja', 'crear') === true;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [proveedor, setProveedor] = useState(null);
  const [view, setView] = useState(null);
  const tabFromQuery = searchParams.get('tab');
  const actionFromQuery = searchParams.get('action');
  const focusPago = actionFromQuery === 'pago';
  const initialTab = location.state?.tab || tabFromQuery || (focusPago ? 'acciones' : 'movimientos');
  const [tab, setTab] = useState(initialTab);
  const pagoCardRef = useRef(null);
  const montoInputRef = useRef(null);
  const [facturaForm, setFacturaForm] = useState({
    numero: '',
    total: location.state?.total_sugerido != null ? String(location.state.total_sugerido) : '',
    vencimiento: '',
    compra_id: location.state?.compra_id || '',
  });
  const [pagoForm, setPagoForm] = useState({
    monto: '',
    origen: 'externo',
    medio_pago: 'transferencia',
    observacion: '',
    referencia: '',
    sucursal_id: '',
    permitir_negativo: false,
  });
  const [pagoConfirmOpen, setPagoConfirmOpen] = useState(false);
  const [ncForm, setNcForm] = useState({ numero: '', total: '', observacion: '' });
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const p = await proveedoresService.obtenerPorId(id);
      setProveedor(p);
      if (canView) {
        const cc = await proveedoresService.obtenerCuentaCorriente(id);
        setView(cc);
      } else {
        setView(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'No se pudo cargar la cuenta corriente';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canView]);

  useEffect(() => {
    if (!focusPago) return;
    setTab('acciones');
  }, [focusPago]);

  useEffect(() => {
    if (!focusPago || tab !== 'acciones' || loading) return;
    const card = pagoCardRef.current;
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (canPay && montoInputRef.current && typeof montoInputRef.current.focus === 'function') {
      montoInputRef.current.focus();
    }
  }, [focusPago, tab, loading, canPay]);

  const registrarFactura = async (e) => {
    e.preventDefault();
    if (!canInvoice) return;
    try {
      setSaving(true);
      await proveedoresService.registrarFactura(id, {
        documentNumber: facturaForm.numero,
        total: parseFloat(facturaForm.total),
        dueDate: facturaForm.vencimiento || undefined,
        purchaseId: facturaForm.compra_id || undefined,
        idempotencyKey: `ui-inv-${id}-${facturaForm.numero}`,
      });
      toast.success('Factura registrada — nace la deuda');
      setFacturaForm({ numero: '', total: '', vencimiento: '', compra_id: '' });
      await cargar();
    } catch (err) {
      toast.error(err.message || 'Error al registrar factura');
    } finally {
      setSaving(false);
    }
  };

  const registrarPago = async (e) => {
    e.preventDefault();
    if (!canPay) return;
    if (pagoForm.origen === 'caja' && !canOperateCaja) {
      toast.error('Sin permiso de caja para pagar desde caja');
      return;
    }
    if (!pagoConfirmOpen) {
      setPagoConfirmOpen(true);
      return;
    }
    try {
      setSaving(true);
      const sucursalId =
        pagoForm.origen === 'caja'
          ? (pagoForm.sucursal_id || sucursalSeleccionada?.id || '')
          : undefined;
      if (pagoForm.origen === 'caja' && !sucursalId) {
        toast.error('Seleccioná una caja / sucursal abierta');
        return;
      }
      await proveedoresService.registrarPagoCxp(id, {
        monto: parseFloat(pagoForm.monto),
        origen: pagoForm.origen,
        medio_pago: pagoForm.medio_pago,
        observacion: pagoForm.observacion,
        referencia: pagoForm.referencia || undefined,
        sucursal_id: sucursalId,
        permitir_negativo: pagoForm.permitir_negativo === true,
        compra_id: location.state?.compra_id || undefined,
        idempotencyKey: `ui-pay-${id}-${pagoForm.monto}-${Date.now()}`,
      });
      toast.success(
        pagoForm.origen === 'caja'
          ? 'Pago desde caja registrado en CxP y egreso de caja'
          : 'Pago externo registrado en CxP (sin movimiento de caja)'
      );
      setPagoForm({
        monto: '',
        origen: 'externo',
        medio_pago: 'transferencia',
        observacion: '',
        referencia: '',
        sucursal_id: sucursalSeleccionada?.id || '',
        permitir_negativo: false,
      });
      setPagoConfirmOpen(false);
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Error al registrar pago');
    } finally {
      setSaving(false);
    }
  };

  const registrarNotaCredito = async (e) => {
    e.preventDefault();
    if (!canInvoice) return;
    try {
      setSaving(true);
      await proveedoresService.registrarNotaCredito(id, {
        documentNumber: ncForm.numero,
        total: parseFloat(ncForm.total),
        motivo: ncForm.observacion || undefined,
        idempotencyKey: `ui-nc-${id}-${ncForm.numero}`,
      });
      toast.success('Nota de crédito registrada');
      setNcForm({ numero: '', total: '', observacion: '' });
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Error al registrar nota de crédito');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-3">
        <Spinner size="lg" />
        <p className="text-sm text-e0e3-muted">Cargando cuenta corriente…</p>
      </div>
    );
  }

  if (loadError && !proveedor) {
    return (
      <div className="space-y-4">
        <p className="text-red-600">{loadError}</p>
        <Button onClick={cargar}>Reintentar</Button>
        <Link to="/proveedores" className="text-e0e3-accent underline ml-3">
          Volver a proveedores
        </Link>
      </div>
    );
  }

  const summary = view?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">
            {proveedor?.nombre || 'Proveedor'} — Cuenta corriente
          </h1>
          <p className="text-sm text-e0e3-muted mt-1">Ledger CxP · saldo reconstruible</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canCreatePurchase && (
            <Link to={`/compras/nueva?proveedorId=${encodeURIComponent(id)}`}>
              <Button color="primary" size="sm">
                Registrar compra
              </Button>
            </Link>
          )}
          <Link to="/finanzas/cuentas-por-pagar">
            <Button color="secondary" size="sm">
              Cuentas por pagar
            </Button>
          </Link>
          <Link to="/proveedores">
            <Button color="secondary" size="sm">
              Volver
            </Button>
          </Link>
          <Link to={`/proveedores/editar/${id}`}>
            <Button color="secondary" size="sm">
              Datos
            </Button>
          </Link>
        </div>
      </div>

      {!canView ? (
        <Card>
          <p className="text-e0e3-muted">
            No tenés permiso para ver la cuenta corriente de proveedores (
            <code>proveedores.cuenta_corriente.ver</code>).
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card>
              <p className="text-xs text-e0e3-muted">Saldo pendiente</p>
              <p className="text-xl font-semibold">{money(summary.saldoPendiente)}</p>
            </Card>
            <Card>
              <p className="text-xs text-e0e3-muted">Saldo vencido</p>
              <p className="text-xl font-semibold">{money(summary.saldoVencido)}</p>
            </Card>
            <Card>
              <p className="text-xs text-e0e3-muted">Próximo vencimiento</p>
              <p className="text-xl font-semibold">{summary.proximoVencimiento || '—'}</p>
            </Card>
            <Card>
              <p className="text-xs text-e0e3-muted">Anticipos disponibles</p>
              <p className="text-xl font-semibold">{money(summary.anticiposDisponibles)}</p>
            </Card>
          </div>

          {location.state?.compra_id && (
            <div className="rounded-lg border border-e0e3-accent/30 bg-e0e3-accent/10 px-4 py-3 text-sm">
              Compra asociada: <code>{location.state.compra_id}</code>. Usá la pestaña{' '}
              <strong>Acciones</strong> para registrar factura y luego el pago.
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {[
              { id: 'movimientos', label: 'Movimientos' },
              { id: 'documentos', label: 'Documentos' },
              { id: 'pagos', label: 'Pagos' },
              { id: 'vencimientos', label: 'Vencimientos' },
              { id: 'acciones', label: 'Acciones' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded text-sm border ${
                  tab === t.id ? 'border-e0e3-accent bg-e0e3-accent/10' : 'border-white/10'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'movimientos' && (
            <Card>
              {!view?.movements?.length ? (
                <p className="text-e0e3-muted">Este proveedor todavía no tiene movimientos.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-e0e3-muted">
                        <th className="py-2 pr-3">Fecha</th>
                        <th className="py-2 pr-3">Tipo</th>
                        <th className="py-2 pr-3">Débito</th>
                        <th className="py-2 pr-3">Crédito</th>
                        <th className="py-2 pr-3">Saldo acum.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {view.movements.map((m) => (
                        <tr key={m.id} className="border-t border-white/10">
                          <td className="py-2 pr-3">{String(m.occurredAt || '').slice(0, 10)}</td>
                          <td className="py-2 pr-3">{m.type}</td>
                          <td className="py-2 pr-3">{m.debit ? money(m.debit) : '—'}</td>
                          <td className="py-2 pr-3">{m.credit ? money(m.credit) : '—'}</td>
                          <td className="py-2 pr-3">{money(m.saldoAcumulado)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {tab === 'documentos' && (
            <Card>
              {!view?.documents?.length ? (
                <p className="text-e0e3-muted">No hay documentos registrados.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {view.documents.map((d) => (
                    <li key={d.id} className="border border-white/10 rounded px-3 py-2">
                      <strong>{d.documentType}</strong> {d.documentNumber} · {money(d.total)} ·{' '}
                      {d.status}
                      {d.dueDate ? ` · vence ${d.dueDate}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {tab === 'pagos' && (
            <Card>
              {!view?.payments?.length ? (
                <p className="text-e0e3-muted">No existen pagos registrados.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {view.payments.map((p) => (
                    <li key={p.id} className="border border-white/10 rounded px-3 py-2">
                      {money(p.amount)} · {p.origen || '—'} · {p.status} ·{' '}
                      {String(p.occurredAt || '').slice(0, 10)}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {tab === 'vencimientos' && (
            <Card>
              <p className="text-xs text-e0e3-muted mb-3">
                Facturas abiertas con fecha de vencimiento (desde documentos CxP).
              </p>
              {!view?.documents?.length ? (
                <p className="text-e0e3-muted">Sin documentos para mostrar vencimientos.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {view.documents
                    .filter((d) => d.dueDate || d.documentType === 'factura' || d.documentType === 'invoice')
                    .map((d) => (
                      <li key={d.id} className="border border-white/10 rounded px-3 py-2 flex justify-between gap-2">
                        <span>
                          <strong>{d.documentNumber || d.id}</strong> · {d.status} ·{' '}
                          {money(d.openBalance != null ? d.openBalance : d.total)}
                        </span>
                        <span className="text-e0e3-muted">
                          vence {d.dueDate || '—'}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </Card>
          )}

          {tab === 'acciones' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <h3 className="font-medium mb-3">Registrar factura</h3>
                {!canInvoice ? (
                  <p className="text-sm text-e0e3-muted">Sin permiso facturas.crear</p>
                ) : (
                  <form className="space-y-2" onSubmit={registrarFactura}>
                    <input
                      className="nexo-field w-full"
                      placeholder="Número"
                      value={facturaForm.numero}
                      onChange={(e) => setFacturaForm((f) => ({ ...f, numero: e.target.value }))}
                      required
                    />
                    <input
                      className="nexo-field w-full"
                      type="number"
                      step="0.01"
                      placeholder="Total"
                      value={facturaForm.total}
                      onChange={(e) => setFacturaForm((f) => ({ ...f, total: e.target.value }))}
                      required
                    />
                    <input
                      className="nexo-field w-full"
                      type="date"
                      value={facturaForm.vencimiento}
                      onChange={(e) => setFacturaForm((f) => ({ ...f, vencimiento: e.target.value }))}
                    />
                    <input
                      className="nexo-field w-full"
                      placeholder="ID compra (opcional)"
                      value={facturaForm.compra_id}
                      onChange={(e) => setFacturaForm((f) => ({ ...f, compra_id: e.target.value }))}
                    />
                    <Button type="submit" loading={saving} color="primary" size="sm">
                      Registrar factura
                    </Button>
                  </form>
                )}
              </Card>
              <div
                id="cxp-registrar-pago"
                ref={pagoCardRef}
                className={focusPago ? 'rounded-lg ring-2 ring-e0e3-accent' : ''}
              >
              <Card>
                <h3 className="font-medium mb-3 text-e0e3-foreground">Registrar pago</h3>
                <p className="text-xs text-e0e3-muted mb-2">
                  Se imputa automáticamente a facturas abiertas (más antiguas primero).
                  Solo el sobrante queda como anticipo.
                </p>
                {!canPay ? (
                  <p className="text-sm text-e0e3-muted">Sin permiso compras.pagar</p>
                ) : (
                  <form className="space-y-3" onSubmit={registrarPago}>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
                      <p className="text-e0e3-muted text-xs">Deuda actual</p>
                      <p className="font-semibold text-e0e3-foreground">{money(summary.saldoPendiente)}</p>
                    </div>
                    <input
                      ref={montoInputRef}
                      className="nexo-field w-full"
                      type="number"
                      step="0.01"
                      placeholder="Importe"
                      value={pagoForm.monto}
                      onChange={(e) => {
                        setPagoConfirmOpen(false);
                        setPagoForm((f) => ({ ...f, monto: e.target.value }));
                      }}
                      required
                    />
                    {pagoForm.monto !== '' && !Number.isNaN(parseFloat(pagoForm.monto)) && (
                      <p className="text-xs text-e0e3-muted">
                        Saldo posterior estimado:{' '}
                        <span className="text-e0e3-foreground font-medium">
                          {money(Math.max(0, (Number(summary.saldoPendiente) || 0) - parseFloat(pagoForm.monto)))}
                        </span>
                      </p>
                    )}
                    <fieldset className="space-y-2">
                      <legend className="text-xs font-medium text-e0e3-foreground mb-1">Origen del pago</legend>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="pago_origen"
                          checked={pagoForm.origen === 'caja'}
                          onChange={() => {
                            setPagoConfirmOpen(false);
                            setPagoForm((f) => ({
                              ...f,
                              origen: 'caja',
                              medio_pago: 'efectivo',
                              sucursal_id: f.sucursal_id || sucursalSeleccionada?.id || '',
                            }));
                          }}
                          disabled={!canOperateCaja}
                        />
                        <span>
                          <span className="block text-sm text-e0e3-foreground">Pago desde caja</span>
                          <span className="block text-xs text-e0e3-muted">
                            El importe se descontará de la caja seleccionada.
                          </span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="pago_origen"
                          checked={pagoForm.origen === 'externo'}
                          onChange={() => {
                            setPagoConfirmOpen(false);
                            setPagoForm((f) => ({
                              ...f,
                              origen: 'externo',
                              medio_pago: 'transferencia',
                            }));
                          }}
                        />
                        <span>
                          <span className="block text-sm text-e0e3-foreground">Pago externo</span>
                          <span className="block text-xs text-e0e3-muted">
                            El pago se registrará en la cuenta corriente sin modificar las cajas del sistema.
                          </span>
                        </span>
                      </label>
                    </fieldset>
                    {pagoForm.origen === 'caja' && (
                      <div className="space-y-2">
                        <label className="block text-xs text-e0e3-muted">Caja / sucursal</label>
                        <select
                          className="nexo-field w-full"
                          value={pagoForm.sucursal_id || sucursalSeleccionada?.id || ''}
                          onChange={(e) => {
                            setPagoConfirmOpen(false);
                            setPagoForm((f) => ({ ...f, sucursal_id: e.target.value }));
                          }}
                          required
                        >
                          {(sucursalesDisponibles || []).map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nombre || s.id}
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center gap-2 text-xs text-e0e3-muted">
                          <input
                            type="checkbox"
                            checked={pagoForm.permitir_negativo === true}
                            onChange={(e) =>
                              setPagoForm((f) => ({ ...f, permitir_negativo: e.target.checked }))
                            }
                          />
                          Permitir saldo negativo (si el contrato lo habilita)
                        </label>
                      </div>
                    )}
                    <select
                      className="nexo-field w-full"
                      value={pagoForm.medio_pago}
                      onChange={(e) => {
                        setPagoConfirmOpen(false);
                        setPagoForm((f) => ({ ...f, medio_pago: e.target.value }));
                      }}
                    >
                      {pagoForm.origen === 'caja' ? (
                        <>
                          <option value="efectivo">Efectivo (caja)</option>
                          <option value="transferencia">Transferencia</option>
                          <option value="tarjeta">Tarjeta</option>
                          <option value="otro">Otro</option>
                        </>
                      ) : (
                        <>
                          <option value="transferencia">Transferencia</option>
                          <option value="banco">Banco</option>
                          <option value="tarjeta_externa">Tarjeta externa</option>
                          <option value="cheque">Cheque</option>
                          <option value="efectivo_externo">Efectivo externo</option>
                          <option value="otro">Otro</option>
                        </>
                      )}
                    </select>
                    <input
                      className="nexo-field w-full"
                      placeholder="Referencia"
                      value={pagoForm.referencia}
                      onChange={(e) => {
                        setPagoConfirmOpen(false);
                        setPagoForm((f) => ({ ...f, referencia: e.target.value }));
                      }}
                    />
                    <input
                      className="nexo-field w-full"
                      placeholder="Observación"
                      value={pagoForm.observacion}
                      onChange={(e) => {
                        setPagoConfirmOpen(false);
                        setPagoForm((f) => ({ ...f, observacion: e.target.value }));
                      }}
                    />
                    {pagoConfirmOpen && (
                      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                        Confirmá: {pagoForm.origen === 'caja' ? 'pago desde caja' : 'pago externo'} por{' '}
                        {money(parseFloat(pagoForm.monto) || 0)}. Volvé a pulsar para registrar.
                      </div>
                    )}
                    <Button type="submit" loading={saving} color="primary" size="sm">
                      {pagoConfirmOpen ? 'Confirmar y registrar' : 'Registrar pago'}
                    </Button>
                  </form>
                )}
              </Card>
              </div>
              <Card>
                <h3 className="font-medium mb-3">Nota de crédito</h3>
                {!canInvoice ? (
                  <p className="text-sm text-e0e3-muted">Sin permiso facturas.crear</p>
                ) : (
                  <form className="space-y-2" onSubmit={registrarNotaCredito}>
                    <input
                      className="nexo-field w-full"
                      placeholder="Número NC"
                      value={ncForm.numero}
                      onChange={(e) => setNcForm((f) => ({ ...f, numero: e.target.value }))}
                      required
                    />
                    <input
                      className="nexo-field w-full"
                      type="number"
                      step="0.01"
                      placeholder="Importe"
                      value={ncForm.total}
                      onChange={(e) => setNcForm((f) => ({ ...f, total: e.target.value }))}
                      required
                    />
                    <input
                      className="nexo-field w-full"
                      placeholder="Observación"
                      value={ncForm.observacion}
                      onChange={(e) => setNcForm((f) => ({ ...f, observacion: e.target.value }))}
                    />
                    <Button type="submit" loading={saving} color="secondary" size="sm">
                      Registrar nota de crédito
                    </Button>
                  </form>
                )}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProveedorCuentaCorriente;
